import { useEffect, useRef, useState } from 'react';

import styles from './LostReasonPrompt.module.css';

interface LostReasonPromptProps {
  open: boolean;
  title?: string;
  entityName?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}

export function LostReasonPrompt({
  open,
  title = 'סמן כ-Lost',
  entityName,
  onCancel,
  onConfirm,
}: LostReasonPromptProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setBusy(false);
      window.setTimeout(() => textareaRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onCancel, open]);

  if (!open) return null;

  async function handleConfirm() {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      textareaRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await onConfirm(trimmed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.backdrop}
        aria-label="סגור"
        onClick={onCancel}
      />
      <div className={styles.panel} role="dialog" aria-modal="true" aria-label={title}>
        <header className={styles.head}>
          <h2>{title}</h2>
          {entityName ? <p className={styles.entity}>{entityName}</p> : null}
        </header>
        <div className={styles.body}>
          <label htmlFor="lost-reason" className={styles.label}>
            סיבה לאיבוד? (חובה)
          </label>
          <textarea
            id="lost-reason"
            ref={textareaRef}
            className={styles.textarea}
            value={reason}
            placeholder="לדוגמה: תקציב לא מאושר, מתחרה זול יותר, אין מענה"
            disabled={busy}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </div>
        <footer className={styles.footer}>
          <button type="button" className={styles.btnSecondary} disabled={busy} onClick={onCancel}>
            בטל
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={busy || reason.trim().length === 0}
            onClick={() => {
              void handleConfirm();
            }}
          >
            סמן כ-Lost
          </button>
        </footer>
      </div>
    </div>
  );
}
